import React, { useMemo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { BarItem } from "@nivo/bar";
import ChartCard from "../ChartCard";
import { getComparisons } from "../../../utils/helpers";
import { propTypes } from "react-bootstrap/esm/Image";
import TEMPORAL_CHART_PALETTE from "./temporalChartPalette";

const MIN_BAR_LENGTH = 2;
// Fixed size (px) for "<1%" bars so they render visibly and stay hoverable
// even though their numeric value is too small to draw at scale.
const MIN_BAR_HEIGHT = 3;

const formatCount = (value) => {
    const count = Number(value);
    return Number.isFinite(count)
        ? count.toLocaleString("en-US", { maximumFractionDigits: 20 })
        : value;
};

const isBelowOneBar = (bar, chartId) => {
    const datum = bar?.data?.data || {};
    return (
        datum[`${bar?.data?.id}Display`] === "<1" ||
        (chartId === "OpenClaimsByDiagnosis" && datum.orgValueDisplay === "<1")
    );
};

const getMinimumSizeBar = (bar, layout, chartId) => {
    if (!isBelowOneBar(bar, chartId)) return bar;
    if (layout === "horizontal") {
        return bar.width < MIN_BAR_HEIGHT
            ? { ...bar, width: MIN_BAR_HEIGHT }
            : bar;
    }
    return bar.height < MIN_BAR_HEIGHT
        ? {
              ...bar,
              y: bar.y + bar.height - MIN_BAR_HEIGHT,
              height: MIN_BAR_HEIGHT,
          }
        : bar;
};

const CustomTooltip = ({
    data,
    id,
    indexValue,
    value,
    comparisons = [],
    chartValueType,
    disabilitySnapshot,
    showPercentOfTotal,
    overallTotal,
}) => {
    if (!disabilitySnapshot && id != null) {
        const name =
            comparisons.find((comparison) => comparison.key === id)?.name ?? id;
        const displayValue = data?.[`${id}Display`] ?? value ?? 0;
        return (
            <div className="dashboard-tooltip">
                {indexValue}: {name} -{" "}
                {chartValueType === "count"
                    ? formatCount(displayValue)
                    : displayValue}
                {chartValueType === "count" ? "" : "%"}
            </div>
        );
    }
    const categoryTotal = Number(data?.totalRecords ?? value) || 0;
    const percentOfTotal =
        overallTotal > 0 ? (categoryTotal / overallTotal) * 100 : 0;
    return (
        <div className="dashboard-tooltip">
            <label>
                {showPercentOfTotal
                    ? `Percent of Total: ${percentOfTotal < 1 ? "<1" : Math.round(percentOfTotal)}%`
                    : `Total count: ${formatCount(categoryTotal)}`}
            </label>
            <div className="tooltip-Arrow"></div>
        </div>
    );
};

CustomTooltip.propTypes = {
    data: PropTypes.any,
    showPercentOfTotal: PropTypes.bool,
    overallTotal: PropTypes.number,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    indexValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.number,
    comparisons: PropTypes.array,
    chartValueType: PropTypes.string,
    disabilitySnapshot: PropTypes.bool,
};

const ReusableBarChart = ({
    chartId,
    title,
    accessibilityText,
    axisLabelOrientation,
    data,
    indexBy,
    groupBy,
    temporalLabels = [],
    selectedCompanyYourCompanyTo,
    selectedFilters,
    organizationName,
    description,
    // Add new props for data passing
    tableData,
    reportDetails,
    layout,
    chartValueOrientation,
    chartValueType,
    disabilitySnapshot,
    totalRecords,
    errorMessage,
    viewId,
    disclaimer,
    // Cache-related props
    cacheKey,
    clientClaimCount,
    initialPeriodsOnAxis = false,
    onToggleChartView,
}) => {
    const [periodsOnAxis, setPeriodsOnAxis] = useState(initialPeriodsOnAxis);
    const isRotate = axisLabelOrientation === "rotate";
    const maxDurationGraph =
        disabilitySnapshot && title === "STD Claims Approaching Max Duration"
            ? true
            : false;

    const comparisonMap = useMemo(
        () => getComparisons(organizationName),
        [organizationName]
    );
    let selectedComparisonText;
    if (selectedCompanyYourCompanyTo?.length === 2) {
        selectedComparisonText = "All";
    } else if (selectedCompanyYourCompanyTo?.length === 1) {
        const value = selectedCompanyYourCompanyTo[0].value;
        if (value === "industryPeers") {
            selectedComparisonText = "Industry Peers";
        } else if (value === "unumBlock") {
            selectedComparisonText = "Unum Clients";
        } else {
            selectedComparisonText = value;
        }
    } else {
        selectedComparisonText = "Organization Only";
    }
    const comparisons = useMemo(
        () => comparisonMap[selectedComparisonText] || [],
        [comparisonMap, selectedComparisonText]
    );
    const isTemporalChart = groupBy === "year" || groupBy === "quarter";
    const canToggleGrouping = !disabilitySnapshot;
    const isTransposed = canToggleGrouping && periodsOnAxis;
    const activeIndexBy = isTransposed ? "temporalLabel" : indexBy;
    const seriesLabels = isTransposed
        ? data.map((item) => String(item[indexBy]))
        : temporalLabels;
    const plotData = isTransposed
        ? (isTemporalChart
              ? temporalLabels.map((label) => ({ key: label, name: label }))
              : comparisons
          ).map((comparison) => {
              const row = { temporalLabel: comparison.name };
              data.forEach((item, index) => {
                  row[`category${index}`] = item[comparison.key] ?? 0;
                  row[`category${index}Display`] =
                      item[`${comparison.key}Display`] ??
                      item[comparison.key] ??
                      0;
              });
              return row;
          })
        : data;
    const chartComparisons =
        isTemporalChart || isTransposed
            ? seriesLabels.map((label, index) => {
                  const paletteEntry =
                      TEMPORAL_CHART_PALETTE[
                          index % TEMPORAL_CHART_PALETTE.length
                      ];
                  return {
                      key: isTransposed ? `category${index}` : label,
                      name: label,
                      ...paletteEntry,
                  };
              })
            : comparisons;

    // For horizontal charts (e.g. "Open claims by diagnosis category") the bars
    // are stacked vertically, so a large number of bars looks clustered at the
    // fixed default height. When there are more than 20 bars we grow the chart
    // height, allocating extra vertical space per additional bar so the data
    // renders cleanly and remains readable.
    const dynamicChartHeight = useMemo(() => {
        if ((isTemporalChart || isTransposed) && layout === "horizontal") {
            return Math.max(
                400,
                plotData.length * chartComparisons.length * 28 + 100
            );
        }
        const barCount = data?.length ?? 0;
        if (layout === "horizontal" && barCount > 20) {
            const baseHeight = 400;
            const perBar = 22;
            return baseHeight + (barCount - 20) * perBar;
        }
        return undefined;
    }, [
        data,
        layout,
        isTemporalChart,
        isTransposed,
        plotData,
        chartComparisons,
    ]);
    const chartMinWidth =
        (isTemporalChart || isTransposed) && layout !== "horizontal"
            ? Math.max(
                  600,
                  plotData.length * (chartComparisons.length * 32 + 8) + 80
              )
            : undefined;

    // Custom layer for Y-axis label at top right
    const ClaimsLabelLayer = useCallback(
        ({ innerWidth }) => (
            <text
                x={innerWidth + 40}
                y={-40}
                textAnchor="end"
                className="charts-claims-label"
            >
                Claims count
            </text>
        ),
        []
    );

    // Wraps the default Nivo bar so tiny non-zero bars (e.g. value 1) stay
    // visible when the axis max is large, while keeping the built-in colors,
    // hover/tooltip and labels intact.
    const MinWidthBarItem = useCallback((barItemProps) => {
        const { bar, style } = barItemProps;
        const value = bar?.data?.value ?? 0;
        if (value > 0 && bar.width < MIN_BAR_LENGTH) {
            return (
                <BarItem
                    {...barItemProps}
                    bar={{ ...bar, width: MIN_BAR_LENGTH }}
                    style={{ ...style, width: MIN_BAR_LENGTH }}
                />
            );
        }
        return <BarItem {...barItemProps} />;
    }, []);

    // Give "<1%" bars a fixed visible size that grows from the baseline, so
    // they render consistently and keep the shared hover tooltip.
    const MinHeightBarItem = useCallback(
        (barItemProps) => {
            const { bar, style } = barItemProps;
            const sizedBar = getMinimumSizeBar(bar, layout, chartId);
            if (sizedBar === bar) {
                return <BarItem {...barItemProps} />;
            }
            return (
                <BarItem
                    {...barItemProps}
                    bar={sizedBar}
                    style={{
                        ...style,
                        width: sizedBar.width,
                        height: sizedBar.height,
                        transform: `translate(${sizedBar.x}, ${sizedBar.y})`,
                    }}
                />
            );
        },
        [layout, chartId]
    );

    const BarLabelLayer = useCallback(
        ({ bars }) => (
            <>
                {bars.map((bar) => {
                    let x, y, textAnchor, alignmentBaseline;
                    // "<1%" bars are enlarged by MinHeightBarItem, so position
                    // labels from the actual rendered bar edge.
                    const renderedBar =
                        chartId === "OpenClaimsByCoverageDetails"
                            ? bar
                            : getMinimumSizeBar(bar, layout, chartId);
                    if (layout === "vertical") {
                        x = bar.x + bar.width / 2;
                        y =
                            renderedBar.y -
                            (chartValueOrientation === "vertical" ? 12 : 6);
                        textAnchor = "middle";
                        alignmentBaseline = "middle";
                    } else {
                        x =
                            bar.x +
                            renderedBar.width +
                            (chartValueOrientation === "vertical" ? 30 : 8);
                        y = bar.y + bar.height / 2;
                        textAnchor = "start";
                        alignmentBaseline = "middle";
                    }

                    const transform =
                        chartValueOrientation === "vertical"
                            ? `rotate(-90, ${x}, ${y})`
                            : undefined;

                    const rawDatum = bar.data?.data || {};
                    const seriesKey = bar.data?.id;
                    const displayField = `${seriesKey}Display`;
                    const displaySource =
                        rawDatum[displayField] ?? bar.data.value;

                    // Determine display value based on chart type
                    let displayValue;
                    if (maxDurationGraph) {
                        displayValue = formatCount(displaySource);
                    } else if (chartValueType === "count") {
                        displayValue = formatCount(displaySource);
                    } else {
                        displayValue = `${displaySource}%`;
                    }

                    return (
                        <text
                            key={bar.key}
                            x={x}
                            y={y}
                            textAnchor={textAnchor}
                            alignmentBaseline={alignmentBaseline}
                            transform={transform}
                            className="charts-inner-text"
                        >
                            {displayValue}
                        </text>
                    );
                })}
            </>
        ),
        [
            layout,
            chartValueOrientation,
            chartValueType,
            maxDurationGraph,
            chartId,
        ]
    );

    const renderTick = useCallback(
        (tick) => {
            const maxCharsPerLine = 22;

            const splitSmart = (text) => {
                if (!text || typeof text !== "string") return [""];

                text = text.trim();
                if (text.length <= maxCharsPerLine) return [text];

                const words = text.split(" ");
                let line = "";
                const lines = [];

                for (const word of words) {
                    if (
                        (line + " " + word).trim().length > maxCharsPerLine &&
                        line.length > 0
                    ) {
                        lines.push(line.trim());
                        line = word;
                    } else {
                        line += " " + word;
                    }
                }

                if (line.trim()) lines.push(line.trim());

                return lines;
            };

            const lines = splitSmart(tick?.value);

            const rotation =
                axisLabelOrientation === "diagonal"
                    ? -45
                    : axisLabelOrientation === "vertical"
                      ? -90
                      : 0;

            const translateY = tick?.y + 20;

            return (
                <g
                    transform={`translate(${tick?.x},${translateY}) rotate(${rotation})`}
                >
                    <text
                        textAnchor={rotation === 0 ? "middle" : "end"}
                        dominantBaseline="middle"
                        className="charts-inner-text"
                    >
                        {lines.map((line, i) => (
                            <tspan x="0" dy={i === 0 ? 0 : "1.1em"} key={i}>
                                {line}
                            </tspan>
                        ))}
                    </text>
                </g>
            );
        },
        [axisLabelOrientation]
    );

    const renderLeftTick = (tick) => {
        const label = tick?.value ?? "";

        return (
            <g transform={`translate(${tick?.x}, ${tick?.y})`}>
                <line
                    x1={0}
                    x2={-5}
                    y1={0}
                    y2={0}
                    stroke="#777777"
                    strokeWidth={1}
                />
                <text
                    x={-10}
                    textAnchor="end"
                    dominantBaseline="central"
                    className="charts-inner-text"
                    // style={{ fontSize: reducedLabelFontSize }}
                >
                    {label}
                </text>
            </g>
        );
    };

    const legendOffsetY = useMemo(
        () =>
            axisLabelOrientation === "diagonal"
                ? 75
                : axisLabelOrientation === "vertical"
                  ? 5
                  : 0,
        [axisLabelOrientation]
    );

    const bottomMargin = useMemo(() => 50 + legendOffsetY, [legendOffsetY]);

    const chartData = useMemo(
        () => (layout === "horizontal" ? [...plotData].reverse() : plotData),
        [layout, plotData]
    );

    const horizontalTickValues = useMemo(() => {
        if (
            layout !== "horizontal" ||
            chartId !== "OpenClaimsByCoverageDetails"
        ) {
            return undefined;
        }

        const maxValue = Math.max(
            0,
            ...data.map((item) => Number(item.orgValue) || 0)
        );

        return maxValue < 10
            ? Array.from({ length: maxValue + 1 }, (_, value) => value)
            : undefined;
    }, [chartId, data, layout]);

    // so labels render on a single line without being cropped.
    const leftMargin = useMemo(() => {
        if (layout !== "horizontal") return 40;
        const longest = (plotData || []).reduce((max, d) => {
            const len = String(d?.[activeIndexBy] ?? "").length;
            return len > max ? len : max;
        }, 0);
        // ~6.5px per character + padding, clamped to a sensible range.
        return Math.min(Math.max(150, longest * 6.5 + 30), 300);
    }, [layout, plotData, activeIndexBy]);

    // Calculate Y-axis tick values for maxDurationGraph (whole numbers only)
    const maxDurationYAxisConfig = useMemo(() => {
        if (!maxDurationGraph || !data || data.length === 0) return {};

        // Find the maximum value in the data
        const maxValue = Math.max(
            ...data.map((d) => parseInt(d.orgValue, 10) || 0)
        );

        // Calculate nice tick values based on data range
        let tickMax;
        let tickCount = 5;

        if (maxValue <= 5) {
            tickMax = 5;
        } else if (maxValue <= 10) {
            tickMax = 10;
        } else if (maxValue <= 20) {
            tickMax = 20;
        } else if (maxValue <= 50) {
            tickMax = 50;
        } else if (maxValue <= 100) {
            tickMax = 100;
        } else if (maxValue <= 200) {
            tickMax = 200;
        } else if (maxValue <= 500) {
            tickMax = 500;
        } else if (maxValue <= 1000) {
            tickMax = 1000;
        } else if (maxValue <= 2000) {
            tickMax = Math.ceil(maxValue / 500) * 500;
        } else if (maxValue <= 5000) {
            tickMax = Math.ceil(maxValue / 1000) * 1000;
        } else {
            tickMax = Math.ceil(maxValue / 2500) * 2500;
        }

        // Generate tick values
        const step = tickMax / tickCount;
        const tickValues = [];
        for (let i = 0; i <= tickCount; i++) {
            tickValues.push(step * i);
        }

        return {
            gridYValues: tickValues,
            maxValue: tickMax,
            tickValues: tickValues,
        };
    }, [maxDurationGraph, data]);

    const getRightMargin = useCallback(() => {
        if (maxDurationGraph || layout === "horizontal") return 60;
        return 30;
    }, [maxDurationGraph, layout]);

    const isCoverageHorizontalChart =
        layout === "horizontal" && chartId === "OpenClaimsByCoverageDetails";

    const CategoryBandsLayer = useCallback(
        ({ xScale, yScale, innerWidth, innerHeight }) => {
            const isHorizontal = layout === "horizontal";
            const indexScale = isHorizontal ? yScale : xScale;
            if (!indexScale?.bandwidth) return null;
            const extent = isHorizontal ? innerHeight : innerWidth;
            const labelPadding = isHorizontal
                ? 48
                : chartValueOrientation === "vertical"
                  ? 40
                  : 24;
            const bandwidth = indexScale.bandwidth();
            const positions = indexScale
                .domain()
                .map((category) => indexScale(category))
                .sort((first, second) => first - second);

            return (
                <g aria-hidden="true" pointerEvents="none">
                    {positions.map((position, index) => {
                        if (index % 2 === 0) return null;
                        const start =
                            (positions[index - 1] + bandwidth + position) / 2;
                        const end =
                            index === positions.length - 1
                                ? extent
                                : (position +
                                      bandwidth +
                                      positions[index + 1]) /
                                  2;
                        return (
                            <rect
                                key={index}
                                x={isHorizontal ? 0 : start}
                                y={isHorizontal ? start : -labelPadding}
                                width={
                                    isHorizontal
                                        ? innerWidth + labelPadding
                                        : end - start
                                }
                                height={
                                    isHorizontal
                                        ? end - start
                                        : innerHeight + labelPadding
                                }
                                fill="#F5F5F5"
                            />
                        );
                    })}
                </g>
            );
        },
        [layout, chartValueOrientation]
    );

    // Build layers array based on chart type
    const chartLayers = useMemo(() => {
        const baseLayers = [
            ...(isTemporalChart || !disabilitySnapshot
                ? [CategoryBandsLayer]
                : []),
            "grid",
            "axes",
            "bars",
            BarLabelLayer,
            "markers",
            "legends",
        ];

        if (maxDurationGraph) {
            return [
                "grid",
                "axes",
                ClaimsLabelLayer,
                "bars",
                BarLabelLayer,
                "markers",
                "legends",
            ];
        }

        return baseLayers;
    }, [
        BarLabelLayer,
        ClaimsLabelLayer,
        maxDurationGraph,
        isTemporalChart,
        disabilitySnapshot,
        CategoryBandsLayer,
    ]);

    const barOptions = useMemo(
        () => ({
            data: chartData,
            keys: chartComparisons.map((comparison) => comparison.key),
            indexBy: activeIndexBy,
            indexScale: {
                type: "band",
                reverse: layout === "horizontal",
            },
            enableGridY: false,
            ...(maxDurationGraph
                ? {
                      enableGridY: true,
                      gridYValues: maxDurationYAxisConfig.tickValues,
                      axisRight: {
                          tickSize: 0,
                          tickPadding: 5,
                          tickValues: maxDurationYAxisConfig.tickValues,
                      },
                      minValue: 0,
                      maxValue: maxDurationYAxisConfig.maxValue,
                      borderColor: "#025780",
                  }
                : {}),
            layout,
            margin: {
                top: maxDurationGraph ? 60 : isRotate ? 90 : 50,
                right: getRightMargin(),
                bottom: bottomMargin,
                left: layout === "horizontal" ? leftMargin : 40,
            },
            axisBottom:
                layout === "horizontal"
                    ? {
                          tickSize: 5,
                          tickPadding: 5,
                          legend: "Count",
                          legendPosition: "middle",
                          legendOffset: 40,
                          ...(horizontalTickValues && {
                              tickValues: horizontalTickValues,
                          }),
                      }
                    : {
                          tickSize: 0,
                          tickPadding: 50,
                          legendPosition: "middle",
                          legendOffset: 40,
                          renderTick,
                      },
            axisLeft:
                layout === "horizontal"
                    ? {
                          tickSize: 5,
                          tickPadding: 8,
                          renderTick: renderLeftTick,
                      }
                    : null,
            barComponent:
                chartId === "OpenClaimsByCoverageDetails"
                    ? MinWidthBarItem
                    : MinHeightBarItem,
            padding: isTemporalChart ? 0.08 : 0.25,
            innerPadding: isTemporalChart ? 2 : isRotate ? 3 : 8,
            groupMode: "grouped",
            borderRadius: 2,
            colors: maxDurationGraph
                ? "#4DB8C1"
                : chartComparisons.map((comparison) => comparison.color),
            borderColor: maxDurationGraph
                ? "#025780"
                : ({ id }) =>
                      chartComparisons.find(
                          (comparison) => comparison.key === id
                      )?.borderColor || "#0199A6",
            borderWidth: 1,
            enableLabel: false,
            isInteractive: true,
            /* sonar-ignore-start */
            tooltip: (tooltipProps) => (
                <CustomTooltip
                    {...tooltipProps}
                    comparisons={chartComparisons}
                    chartValueType={chartValueType}
                    disabilitySnapshot={disabilitySnapshot}
                    showPercentOfTotal={
                        chartId === "OpenClaimsByCoverageDetails"
                    }
                    overallTotal={totalRecords}
                />
            ),
            /* sonar-ignore-end */
            layers: chartLayers,
            theme: {
                axis: isCoverageHorizontalChart
                    ? {
                          domain: {
                              line: {
                                  stroke: "#777777",
                                  strokeWidth: 1,
                              },
                          },
                          ticks: {
                              line: {
                                  stroke: "#777777",
                                  strokeWidth: 1,
                              },
                              text: {
                                  fontSize: 12,
                                  fill: "#525252",
                              },
                          },
                          legend: {
                              text: {
                                  fontSize: 12,
                                  fill: "#525252",
                              },
                          },
                      }
                    : {},
                legends: {
                    text: {
                        fontSize: 12,
                        fontWeight: 400,
                        fill: "#525252",
                    },
                },
            },
            legends: undefined,
            animate: true,
            motionConfig: isTemporalChart ? { duration: 350 } : "gentle",
            motionStiffness: 90,
            motionDamping: 15,
        }),
        [
            chartData,
            chartComparisons,
            activeIndexBy,
            isTemporalChart,
            layout,
            isRotate,
            legendOffsetY,
            bottomMargin,
            chartLayers,
            renderTick,
            getRightMargin,
            maxDurationGraph,
            chartValueType,
            maxDurationYAxisConfig,
            disabilitySnapshot,
            leftMargin,
            horizontalTickValues,
            chartId,
            MinWidthBarItem,
            MinHeightBarItem,
            totalRecords,
            isCoverageHorizontalChart,
        ]
    );

    // Now handle early return for error
    if (data?.length === 0 || errorMessage) {
        return (
            <ChartCard
                title={title}
                description={description}
                accessibilityText={accessibilityText}
                selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
                selectedFilters={selectedFilters}
                variant="bar"
                chartOptions={{}}
                isRotate={isRotate}
                errorMessage={errorMessage || "No data available"}
                disabilitySnapshot={disabilitySnapshot}
                totalRecords={totalRecords}
                viewId={viewId}
                tableData={tableData}
                reportDetails={reportDetails}
                comparisons={chartComparisons}
                legendOffsetY={legendOffsetY}
                disclaimer={disclaimer}
                clientClaimCount={clientClaimCount}
                chartHeight={dynamicChartHeight}
            />
        );
    }

    return (
        <ChartCard
            title={title}
            description={description}
            accessibilityText={accessibilityText}
            selectedCompanyYourCompanyTo={selectedCompanyYourCompanyTo}
            selectedFilters={selectedFilters}
            variant="bar"
            chartOptions={barOptions}
            percentageGroups={
                !disabilitySnapshot &&
                ["percent", "percentage"].includes(chartValueType)
                    ? (isTemporalChart
                          ? temporalLabels
                          : comparisons.map((item) => item.key)
                      ).map((key) => data.map((item) => item[key]))
                    : []
            }
            groupBy={groupBy}
            onToggleGrouping={
                canToggleGrouping
                    ? onToggleChartView ||
                      (() => setPeriodsOnAxis((previous) => !previous))
                    : undefined
            }
            groupingSwapped={isTransposed}
            chartMinWidth={chartMinWidth}
            isRotate={isRotate}
            disabilitySnapshot={disabilitySnapshot}
            totalRecords={totalRecords}
            viewId={viewId}
            tableData={tableData}
            reportDetails={reportDetails}
            comparisons={chartComparisons}
            legendOffsetY={legendOffsetY}
            disclaimer={disclaimer}
            cacheKey={cacheKey}
            clientClaimCount={clientClaimCount}
            chartHeight={dynamicChartHeight}
        />
    );
};

ReusableBarChart.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    accessibilityText: PropTypes.string,
    organizationName: PropTypes.string,
    axisLabelOrientation: PropTypes.string,
    selectedCompanyYourCompanyTo: PropTypes.array,
    selectedFilters: PropTypes.object,
    data: PropTypes.arrayOf(
        PropTypes.objectOf(
            PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        )
    ).isRequired,
    indexBy: PropTypes.string,
    groupBy: PropTypes.string,
    temporalLabels: PropTypes.arrayOf(PropTypes.string),
    initialPeriodsOnAxis: PropTypes.bool,
    onToggleChartView: PropTypes.func,
    chartId: PropTypes.string,
    layout: PropTypes.string,
    chartValueOrientation: PropTypes.string,
    chartValueType: PropTypes.string,
    disabilitySnapshot: PropTypes.bool,
    totalRecords: PropTypes.number,
    errorMessage: PropTypes.string,
    viewId: PropTypes.string,
    disclaimer: PropTypes.string,
    clientClaimCount: PropTypes.number,
};

export default ReusableBarChart;
